import { useEffect, useState } from "react";
import { CloseCircle } from "@solar-icons/react/ssr";
import {
    Button,
    ListItem,
    ListItemText,
    Box,
    Chip,
    useTheme,
    Grid,
    Tooltip,
    ListItemButton,
} from "@mui/material";
import AiChatSingleThreaded from "@/ai/AiChatSingleThreaded";
import AiLogo from "@/ai/AiLogo";
import { request } from "@/Api";
import { CustomBox } from "@/common/StyledComponents";
import { truncateString } from "@/utils";

export default function AiChat() {
    const theme = useTheme();
    const [threads, setThreads] = useState([]);
    const [threadId, setThreadId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(true);

    const popupWidth = "90vw";
    const maxPopupWidth = 1100;
    const drawerWidth = 220;

    const getThreads = () => {
        request.get("/threads").then((response) => {
            setThreads(response.data);
        });
    };

    const handleSelectThread = (id) => {
        setThreadId(id);
    };

    const handleNewThread = () => {
        setThreadId(null);
    };

    const handleNewThreadCreated = (threadId) => {
        setThreadId(threadId);
        getThreads();
    };

    useEffect(() => {
        getThreads();
    }, []);

    return (
        <CustomBox
            height="80vh"
            sx={{
                width: popupWidth,
                background: theme.palette.background.default,
                display: "flex",
            }}
        >
            {/* Sidebar */}
            {drawerOpen && (
                <Box
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        borderRight: `1px solid ${theme.palette.divider}`,
                        borderRadius: "20px 0 0 20px",
                    }}
                >
                    <Box sx={{ padding: 2 }}>
                        <Box
                            sx={{
                                paddingBottom: 2,
                                borderBottom: 1,
                                borderColor: "divider",
                            }}
                        >
                            <Grid spacing={1} container alignItems={"center"}>
                                <Grid item>
                                    <AiLogo />
                                </Grid>
                                <Grid item>
                                    <span style={{ fontWeight: "bold" }}>
                                        AI Assistant
                                    </span>
                                </Grid>
                            </Grid>
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={handleNewThread}
                            sx={{ marginTop: 3, width: "100%" }}
                        >
                            New Chat +
                        </Button>
                    </Box>
                    <Box sx={{ flex: 1, overflow: "auto" }}>
                        <Threads
                            threads={threads}
                            width={drawerWidth}
                            selectedThreadId={threadId}
                            connectThread={handleSelectThread}
                        />
                    </Box>
                    <Box sx={{ padding: 1 }}>
                        <Button
                            onClick={() => setDrawerOpen(false)}
                            color="secondary"
                            variant="contained"
                            endIcon={<CloseCircle />}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Chat area */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: "100%",
                    transition: "all 0.3s",
                }}
            >
                <AiChatSingleThreaded
                    toolSet="general"
                    defaultThreadId={threadId}
                    onNewThread={handleNewThreadCreated}
                />
            </Box>
        </CustomBox>
    );
}

const Threads = ({ threads, width, selectedThreadId, connectThread }) => {
    const theme = useTheme();
    const handleThreadClick = (thread) => {
        connectThread(thread.id);
    };
    return (
        <div style={{ width: width, overflow: "auto" }}>
            {threads.map((thread) => (
                <Tooltip
                    title={truncateString(thread.title, 120)}
                    key={thread.id}
                    componentsProps={{
                        tooltip: { sx: { pointerEvents: "none" } },
                        popper: { sx: { pointerEvents: "none" } },
                    }}
                >
                    <ListItem disablePadding>
                        <ListItemButton
                            sx={{
                                paddingLeft: 1.5,
                                paddingRight: 2,
                                paddingTop: 0,
                                paddingBottom: 0,
                            }}
                            disableGutters
                            onClick={() => handleThreadClick(thread)}
                        >
                            <ListItemText
                                primaryTypographyProps={{
                                    style: {
                                        fontSize: 14,
                                        padding: 1,
                                        paddingLeft: 5,
                                    },
                                }}
                                sx={{
                                    textWrap: "nowrap",
                                    overflow: "auto",
                                    textOverflow: "ellipsis",
                                }}
                                primary={
                                    <span
                                        style={{
                                            color:
                                                selectedThreadId == thread.id &&
                                                theme.palette.primary.main,
                                        }}
                                    >
                                        {thread.title}
                                    </span>
                                }
                            />
                        </ListItemButton>
                    </ListItem>
                </Tooltip>
            ))}
        </div>
    );
};
